import {
  ScanSearch, Wind, Thermometer, FlaskConical, ShieldCheck, ClipboardList,
  Camera, Droplets, Building2, Home, Leaf, Award, PhoneCall, FileCheck2
} from 'lucide-react';

export const COMPANY = {
  name: '360 Mold Services',
  logo: '/assets/360moldlogo.png',
  phone: '(226) 972-6440',
  phoneHref: 'tel:+12269726440',
  email: 'info@360inspectionservices.ca',
  address: '369-55 Northfield Drive E, Waterloo, Ontario, N2K 3T6',
  region: 'Southern Ontario',
  established: 2012,
  tagline: 'Independent Mold & Indoor Air Quality Testing',
};

export const IMAGES = {
  heroHome: 'https://images.unsplash.com/photo-1656122381069-9ec666d95cf1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
  familyHome: 'https://images.unsplash.com/photo-1758598737810-7489fde716af?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  sunlitRoom: 'https://images.unsplash.com/photo-1649511134921-67afc567280c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  plantsInterior: 'https://images.unsplash.com/photo-1583753075968-1236ccb83c66?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  cleanInterior: 'https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  moldInspection: 'https://images.unsplash.com/photo-1579830780149-0a7a0d8114ea?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  lab: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
};

export const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Mold Inspection', to: '/mold-inspection' },
  { label: 'Air Quality', to: '/air-quality' },
  { label: 'Service Areas', to: '/service-areas' },
  { label: 'Contact', to: '/contact' },
];

export const TRUST_BADGES = [
  { abbr: 'IAC2', name: 'Certified Indoor Air Consultants', note: 'Mold & IAQ certified' },
  { abbr: 'InterNACHI', name: 'Intl. Assoc. of Certified Home Inspectors', note: 'Member in good standing' },
  { abbr: 'A2LA', name: 'Accredited Laboratory Partners', note: 'ISO/IEC 17025 labs' },
  { abbr: 'IR', name: 'Infrared Thermography Certified', note: '400+ training hours' },
];

export const STATS = [
  { value: '2,500+', label: 'Inspections Completed' },
  { value: '12+', label: 'Years Serving Ontario' },
  { value: '400+', label: 'Hours of Certified Training' },
  { value: '~1 Week', label: 'Detailed Report Turnaround' },
];

export const SERVICES = [
  {
    icon: ScanSearch,
    slug: 'mold-inspection',
    title: 'Mold Inspection Services',
    short: 'Certified visual inspections and testing to locate mold and its moisture source — with a clear, customized report.',
    to: '/mold-inspection',
  },
  {
    icon: Wind,
    slug: 'air-quality',
    title: 'Indoor Air Quality Testing',
    short: 'Air and surface sampling analyzed by accredited labs to reveal what you are really breathing at home or work.',
    to: '/air-quality',
  },
  {
    icon: Thermometer,
    slug: 'thermal',
    title: 'Infrared Thermal Imaging',
    short: 'Non-invasive IR cameras detect hidden moisture and air leaks behind walls, ceilings and floors.',
    to: '/mold-inspection',
  },
  {
    icon: FlaskConical,
    slug: 'asbestos',
    title: 'Asbestos Testing',
    short: 'Sampling and lab analysis for suspect materials in older residential and commercial properties.',
    to: '/contact',
  },
  {
    icon: Building2,
    slug: 'grow-ops',
    title: 'Marijuana Grow-Op Inspections',
    short: 'Specialized assessments for properties with a history of grow-op moisture and mold damage.',
    to: '/contact',
  },
  {
    icon: ShieldCheck,
    slug: 'clearance',
    title: 'Post-Remediation Clearance',
    short: 'Independent verification that remediation was successful — done right, the first time.',
    to: '/mold-inspection',
  },
];

export const PROCESS = [
  { icon: PhoneCall, title: 'Book & Consult', desc: 'Call or request a quote 24/7. We discuss your concerns and schedule a visit that suits you.' },
  { icon: ScanSearch, title: 'On-Site Assessment', desc: 'Visual inspection, thermal imaging, moisture mapping and air/surface sampling by a certified inspector.' },
  { icon: FlaskConical, title: 'Independent Lab Analysis', desc: 'Samples are sent to accredited 3rd-party labs — no conflict of interest, ever.' },
  { icon: FileCheck2, title: 'Clear Report & Plan', desc: 'Within about a week you receive an easy-to-understand report and, if needed, a scope of work.' },
];

export const WHY_US = [
  { icon: ShieldCheck, title: 'Truly Independent', desc: 'We only inspect and test — never remediate. Our findings are always in your best interest.' },
  { icon: Award, title: 'IAC2 & Infrared Certified', desc: 'Inspectors trained to InterNACHI & IAC2 Standards of Practice with 400+ hours of instruction.' },
  { icon: FlaskConical, title: 'Accredited Lab Partners', desc: 'All samples analyzed by A2LA-accredited (ISO/IEC 17025) third-party laboratories.' },
  { icon: ClipboardList, title: 'Reports You Understand', desc: 'Plain-language findings with digital photos, lab interpretation and clear recommendations.' },
];

export const MOLD_STEPS = [
  { icon: ScanSearch, title: 'Visual Inspection', desc: 'A thorough walk-through looking for conditions conducive to mold growth — the starting point of every assessment.' },
  { icon: Camera, title: 'Digital Documentation', desc: 'We photograph every area of concern so you see exactly what we saw during the inspection.' },
  { icon: Thermometer, title: 'Thermal Imaging', desc: 'Infrared cameras detect temperature differences that reveal damp or wet building materials.' },
  { icon: Droplets, title: 'Moisture Mapping', desc: 'We locate materials wet enough to support mold and confirm whether leaks are active or resolved.' },
  { icon: Wind, title: 'Air Quality Testing', desc: 'Indoor air samples — plus an outdoor control — measure whether spore levels are higher than they should be.' },
  { icon: FlaskConical, title: 'Surface & Inner-Wall Testing', desc: 'Swabs, tape lifts and cavity sampling confirm mold behind walls without unnecessary damage.' },
  { icon: ClipboardList, title: 'Mold Assessment Report', desc: 'A clear report with lab interpretation and written strategies, delivered within about a week.' },
  { icon: FileCheck2, title: 'Scope of Work', desc: 'If required, a remediation plan with cleanup suggestions and preventative measures for your property.' },
];

export const CERTIFICATIONS = [
  'InterNACHI-Certified Inspector',
  'IAC2-Certified Mold Inspector',
  'Infrared Thermography Inspection Training',
  'Indoor Air Quality Training',
  'Building Science & Thermal Imaging Training',
  'Defect Recognition & Report Writing',
  'Works within InterNACHI & IAC2 Standards',
  'Abides by the InterNACHI Code of Ethics',
];

export const SERVICE_AREAS = [
  'Ajax','Aurora','Barrie','Brampton','Brantford','Burlington','Caledon','Cambridge',
  'Collingwood','Erin','Fort Erie','Guelph','Halton Hills','Hamilton','Ingersoll','Innisfil',
  'Kitchener','London','Markham','Milton','Mississauga','Muskoka','Newmarket','Niagara Falls',
  'Norfolk County','Oakville','Orangeville','Orillia','Oshawa','Owen Sound','Peterborough',
  'Pickering','Port Severn','Richmond Hill','Shelburne','St. Catharines','St. Thomas','Stratford',
  'Tillsonburg','Thorold','Toronto','Vaughan','Wasaga Beach','Waterloo','Whitby','Woodstock',
];

export const PRIMARY_AREAS = ['Kitchener','Waterloo','Cambridge','Guelph','Brantford','London','Toronto','Peterborough'];

export const slugify = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

// A small pool of intro variations so each city page reads uniquely
export const CITY_INTROS = [
  'Worried about mold or the air quality in your {CITY} home or business? Our IAC2-certified inspectors deliver objective, lab-backed answers — never a sales pitch.',
  'From damp basements to hidden leaks, {CITY} properties face real moisture challenges. We find the source and tell you exactly what\u2019s going on — independently.',
  'Serving {CITY} and the surrounding area, we combine thermal imaging, moisture mapping and accredited lab testing to give you a clear picture of your indoor environment.',
  'Whether you\u2019re buying, selling or simply concerned, our {CITY} mold and indoor air quality assessments give you trustworthy, easy-to-understand results.',
  'Mold problems don\u2019t fix themselves. Our certified inspectors help {CITY} homeowners and businesses identify moisture and mold issues before they get worse.',
];

export const cityIntro = (city) => {
  const idx = city.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % CITY_INTROS.length;
  return CITY_INTROS[idx].replaceAll('{CITY}', city);
};

// Nearby suggestions for a given city (simple neighbouring slice from the list)
export const nearbyCities = (city, all, count = 6) => {
  const i = all.indexOf(city);
  if (i === -1) return all.slice(0, count);
  const out = [];
  let step = 1;
  while (out.length < count && step < all.length) {
    if (all[i - step]) out.push(all[i - step]);
    if (out.length < count && all[i + step]) out.push(all[i + step]);
    step += 1;
  }
  return out;
};

export const TESTIMONIALS = [
  { name: 'Sarah M.', location: 'Kitchener, ON', text: 'After a basement flood we were worried about mold. The inspector was thorough, explained everything, and the report was incredibly clear. Total peace of mind.', rating: 5 },
  { name: 'David R.', location: 'Waterloo, ON', text: 'Loved that they only do testing — no pressure to sell remediation. The thermal imaging found a hidden leak we never knew about.', rating: 5 },
  { name: 'Priya K.', location: 'Guelph, ON', text: 'Professional from the first call to the final lab report. We finally understood our air quality issues and what to do next.', rating: 5 },
  { name: 'Michael T.', location: 'Cambridge, ON', text: 'Bought an older home and wanted certainty. Fast turnaround, honest advice and genuinely friendly service. Highly recommend.', rating: 5 },
];

export const FAQS = [
  { q: 'Do you also remove or remediate the mold?', a: 'No — and that is intentional. We only perform inspections and testing so our findings stay completely objective. If remediation is needed we provide a scope of work and can refer trusted certified contractors.' },
  { q: 'How long until I get my report?', a: 'Most detailed assessment reports are delivered within about one week after your inspection, including interpretation of the independent lab results.' },
  { q: 'Where do my samples get analyzed?', a: 'All air and surface samples are sent to independent A2LA-accredited (ISO/IEC 17025) third-party laboratories to ensure accurate, unbiased results.' },
  { q: 'Do I always need surface testing?', a: 'Not always. If mold growth is visibly obvious, surface testing may not be necessary. Your inspector will recommend only the testing that genuinely helps your situation.' },
  { q: 'What areas do you serve?', a: 'We serve Southern Ontario, including Kitchener, Waterloo, Cambridge, Guelph, Brantford, London, the Greater Toronto Area, Peterborough and many more communities.' },
];

export const AIR_POLLUTANTS = [
  { icon: Leaf, title: 'Mold Spores', desc: 'Elevated indoor spore counts can trigger allergies, asthma and respiratory irritation.' },
  { icon: Droplets, title: 'Hidden Moisture', desc: 'Damp building materials feed mold growth and often hide behind walls and ceilings.' },
  { icon: Home, title: 'Everyday Pollutants', desc: 'Chemicals, gases and biologicals can cause headaches, fatigue and long-term health effects.' },
];
